/* ==========================================================================
   NGA — Product gallery

   Desktop: thumbnail rail drives a single stage frame.
   Mobile:  the stage is a scroll-snap carousel and the dots follow it.

   One `select(i)` owns all state, so the rail, stage, dots and counter can
   never drift out of sync — which is what went wrong in the first build.
   ========================================================================== */
(function () {
  'use strict';

  var reduce = window.matchMedia('(prefers-reduced-motion: reduce)');
  var mobile = window.matchMedia('(max-width: 749px)');

  function init(root) {
    (root || document).querySelectorAll('[data-nga-gallery]:not([data-nga-gallery-done])').forEach(function (gal) {
      gal.setAttribute('data-nga-gallery-done', '');

      var frames = Array.prototype.slice.call(gal.querySelectorAll('[data-nga-gal-frame]'));
      if (!frames.length) return;

      var thumbs = Array.prototype.slice.call(gal.querySelectorAll('[data-nga-gal-thumb]'));
      var strip = gal.querySelector('[data-nga-gal-frames]');
      var prev = gal.querySelector('[data-nga-gal-prev]');
      var next = gal.querySelector('[data-nga-gal-next]');
      var counter = gal.querySelector('[data-nga-gal-current]');

      // Dots and the lightbox sit outside the gallery element.
      var scope = gal.closest('.nga-pdp__gallery') || gal.parentElement || document;
      var dots = Array.prototype.slice.call(scope.querySelectorAll('[data-nga-gal-dot]'));
      var box = scope.querySelector('[data-nga-zoombox]');

      var index = 0;

      function select(i, opts) {
        opts = opts || {};
        if (i < 0) i = frames.length - 1;
        if (i >= frames.length) i = 0;
        index = i;

        frames.forEach(function (f, n) {
          f.classList.toggle('is-active', n === i);
          // Never leave a video playing on a frame that is no longer shown.
          var v = f.querySelector('video');
          if (v && n !== i && !v.paused) v.pause();
        });
        thumbs.forEach(function (t, n) { t.setAttribute('aria-selected', n === i ? 'true' : 'false'); });
        dots.forEach(function (d, n) { d.classList.toggle('is-active', n === i); });
        if (counter) counter.textContent = i + 1;

        // Keep the active thumb visible in a long rail.
        var thumb = thumbs[i];
        if (thumb && !opts.fromScroll) thumb.scrollIntoView({ block: 'nearest', inline: 'nearest' });

        // On mobile the stage is the scroller — drive it unless it drove us.
        if (mobile.matches && strip && !opts.fromScroll) {
          var target = frames[i];
          if (target) {
            strip.scrollTo({
              left: target.offsetLeft - strip.offsetLeft,
              behavior: reduce.matches ? 'auto' : 'smooth'
            });
          }
        }
      }

      thumbs.forEach(function (t, n) { t.addEventListener('click', function () { select(n); }); });
      dots.forEach(function (d, n) { d.addEventListener('click', function () { select(n); }); });
      if (prev) prev.addEventListener('click', function () { select(index - 1); });
      if (next) next.addEventListener('click', function () { select(index + 1); });

      if (strip) {
        var t;
        strip.addEventListener('scroll', function () {
          if (!mobile.matches) return;
          clearTimeout(t);
          t = setTimeout(function () {
            var mid = strip.scrollLeft + strip.clientWidth / 2;
            var nearest = 0, best = Infinity;
            frames.forEach(function (f, n) {
              var c = f.offsetLeft - strip.offsetLeft + f.offsetWidth / 2;
              var d = Math.abs(c - mid);
              if (d < best) { best = d; nearest = n; }
            });
            select(nearest, { fromScroll: true });
          }, 90);
        }, { passive: true });
      }

      gal.addEventListener('keydown', function (e) {
        if (e.key === 'ArrowRight') { e.preventDefault(); select(index + 1); }
        if (e.key === 'ArrowLeft') { e.preventDefault(); select(index - 1); }
      });

      /* ---------------- Zoom ---------------- */
      if (box) {
        var viewport = box.querySelector('[data-nga-zoombox-viewport]');

        function openZoom() {
          var img = frames[index] && frames[index].querySelector('img');
          if (!img) return;
          var full = img.getAttribute('data-zoom-src') || img.currentSrc || img.src;
          viewport.innerHTML = '<img src="' + full + '" alt="' +
            String(img.alt || '').replace(/"/g, '&quot;') + '">';
          box.classList.add('is-open');
          document.body.classList.add('nga-no-scroll');
          var close = box.querySelector('[data-nga-zoombox-close]');
          if (close) close.focus();
        }
        function closeZoom() {
          box.classList.remove('is-open');
          document.body.classList.remove('nga-no-scroll');
          viewport.innerHTML = '';
        }

        gal.addEventListener('click', function (e) {
          if (e.target.closest('[data-nga-hotspot-dot]')) return;
          if (e.target.closest('[data-nga-gal-prev], [data-nga-gal-next], [data-nga-gal-thumb]')) return;
          if (!window.matchMedia('(min-width: 990px)').matches) return;
          if (!e.target.closest('.nga-gal__frame--zoom')) return;
          openZoom();
        });

        box.addEventListener('click', function (e) {
          if (e.target.closest('[data-nga-zoombox-close]') || e.target === viewport || e.target.tagName === 'IMG') closeZoom();
        });
        document.addEventListener('keydown', function (e) {
          if (e.key === 'Escape' && box.classList.contains('is-open')) closeZoom();
        });
      }

      /* ---------------- Hotspots ---------------- */
      gal.addEventListener('click', function (e) {
        var dot = e.target.closest('[data-nga-hotspot-dot]');
        if (!dot) return;
        e.preventDefault();
        e.stopPropagation();
        var spot = dot.closest('.nga-hotspot');
        var wasOpen = spot.classList.contains('is-open');
        gal.querySelectorAll('.nga-hotspot.is-open').forEach(function (s) { s.classList.remove('is-open'); });
        if (!wasOpen) spot.classList.add('is-open');
      });

      /* ---------------- Autoplay ----------------

         The wireframe promises "AUTOPLAY ON · HOVER TO PAUSE" with a segmented
         rail and a pause control. The markup was there; the behaviour was not.

         It pauses on hover, on focus, while a video frame is showing, when the
         tab is hidden, and permanently once the shopper takes control — either
         with the pause button or by picking a thumbnail. Advancing the gallery
         under someone who is studying an image is worse than not advancing. */
      var segs = Array.prototype.slice.call(gal.querySelectorAll('[data-nga-gal-seg]'));
      var pauseBtn = gal.querySelector('[data-nga-gal-pause]');
      var autoState = gal.querySelector('[data-nga-gal-autostate]');
      /* A delay of 0 means the gallery does not advance on its own. Autoplay
         moving the image out from under someone mid-scroll is worse than
         making them tap, so it is off unless the merchant turns it on. */
      var raw = parseInt(gal.getAttribute('data-autoplay-delay'), 10);
      var delay = isNaN(raw) ? 4500 : raw;

      var timer = null;
      var stopped = frames.length < 2 || reduce.matches || delay < 1;
      var held = 0;

      function paintSegs() {
        segs.forEach(function (s, n) { s.classList.toggle('is-active', n === index); });
      }

      function setState(on) {
        if (!autoState) return;
        autoState.textContent = autoState.getAttribute(on ? 'data-on' : 'data-off') || (on ? 'On' : 'Off');
      }

      function tick() {
        clearTimeout(timer);
        if (stopped || held > 0) return;
        timer = setTimeout(function () {
          // Let a playing video finish rather than yanking it away.
          var vid = frames[index] && frames[index].querySelector('video');
          if (vid && !vid.paused) { tick(); return; }
          select(index + 1);
          tick();
        }, delay);
      }

      function hold() { held += 1; clearTimeout(timer); }
      function release() { held = Math.max(0, held - 1); if (!held) tick(); }

      function stop() {
        stopped = true;
        clearTimeout(timer);
        if (pauseBtn) pauseBtn.setAttribute('aria-pressed', 'true');
        setState(false);
      }

      if (stopped && pauseBtn) pauseBtn.setAttribute('aria-pressed', 'true');

      function start() {
        // Turning it on by hand is allowed even when it is off by default.
        if (delay < 1) delay = 4500;
        stopped = false;
        if (pauseBtn) pauseBtn.setAttribute('aria-pressed', 'false');
        setState(true);
        tick();
      }

      if (!stopped) {
        gal.addEventListener('mouseenter', hold);
        gal.addEventListener('mouseleave', release);
        gal.addEventListener('focusin', hold);
        gal.addEventListener('focusout', release);

        document.addEventListener('visibilitychange', function () {
          if (document.hidden) hold();
          else release();
        });

        // Taking control ends autoplay for good.
        thumbs.forEach(function (t) { t.addEventListener('click', stop); });
        dots.forEach(function (d) { d.addEventListener('click', stop); });
        if (prev) prev.addEventListener('click', stop);
        if (next) next.addEventListener('click', stop);

        if (pauseBtn) {
          pauseBtn.addEventListener('click', function () {
            if (stopped) start();
            else stop();
          });
        }

        setState(true);
        tick();
      } else {
        setState(false);
        if (pauseBtn) pauseBtn.setAttribute('aria-pressed', 'true');
      }

      // Keep the rail in step with every selection, autoplay or not.
      var baseSelect = select;
      select = function (i, opts) {
        baseSelect(i, opts);
        paintSegs();
      };
      paintSegs();

      /* ---------------- Variant change jumps to its image ---------------- */
      document.addEventListener('nga:variant:change', function (e) {
        var mediaId = e.detail && e.detail.mediaId;
        if (!mediaId) return;
        var found = -1;
        frames.forEach(function (f, n) {
          if (f.getAttribute('data-media-id') === String(mediaId)) found = n;
        });
        if (found > -1) select(found);
      });

      select(0);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { init(document); }, { once: true });
  } else {
    init(document);
  }
  document.addEventListener('shopify:section:load', function (e) { init(e.target); });
})();
